import {
  Button,
  DetailField,
  Divider,
  EmptyState,
  LineItemList,
  Loading,
  MobileHeader,
  MobileScreen,
  Stack,
  Text,
  toneForStatus,
  sentenceCase,
} from '@relay-towing/ui';
import { ExternalLink } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useInvoice } from '../../hooks/useInvoice';
import { openExternal } from '../../lib/nativeBridge';
import { useBack } from '../shell/useBack';
import { vehicleOf } from '../vehicle';

const money = (amount: number | null | undefined) =>
  typeof amount === 'number'
    ? amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : undefined;

const day = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString() : undefined;

/**
 * One invoice, to read, laid out as a call and an impound are: its total
 * and who it is for at the top, with its status as the header's chip, then
 * Details and Charges, each ruled off from the next, and the invoice itself
 * to open. No Figma frame draws an invoice; it follows the call's (915-1575
 * for the impound beside it). Payment is taken from the call it bills
 * (CallPaymentScreen). The old screen showed today's date as "Created";
 * this shows the date it was issued.
 */
export function InvoiceDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBack('/invoices');
  const { data: invoice, isLoading } = useInvoice(id);

  const header = (
    <MobileHeader
      onBack={back}
      backLabel="Back to invoices"
      status={
        invoice
          ? {
              label: sentenceCase(invoice.status),
              tone: toneForStatus(invoice.status),
            }
          : undefined
      }
      title={invoice ? undefined : 'Invoice'}
    />
  );

  if (isLoading) return <Loading label="Loading the invoice" fill="screen" />;
  if (!invoice)
    return (
      <MobileScreen header={header}>
        <EmptyState
          title="This invoice is not available"
          description="It may have been deleted, or it is not one your role can see."
        />
      </MobileScreen>
    );

  const vehicle = vehicleOf({
    vehicleYear: invoice.vehicleYear,
    vehicleMake: invoice.vehicleMake,
    vehicleModel: invoice.vehicleModel,
  });

  return (
    <MobileScreen header={header}>
      <Stack gap={6}>
        <Stack gap={1}>
          <Text as="h1" variant="h1" numeric>
            {money(invoice.total)}
          </Text>
          {invoice.clientName ? (
            <Text tone="muted">{invoice.clientName}</Text>
          ) : null}
          <Text tone="muted">{invoice.invoiceNumber}</Text>
        </Stack>
        <Divider />
        <Stack gap={4}>
          <Text as="h2" variant="h2">
            Details
          </Text>
          {invoice.issuedOn ? (
            <DetailField label="Issued" value={day(invoice.issuedOn)} />
          ) : null}
          {invoice.dueDate ? (
            <DetailField label="Due" value={day(invoice.dueDate)} />
          ) : null}
          <DetailField
            label="Payment"
            value={invoice.isCashPayment ? 'Cash' : 'Card'}
          />
          {vehicle ? <DetailField label="Vehicle" value={vehicle} /> : null}
          {invoice.licensePlate ? (
            <DetailField label="Plate" value={invoice.licensePlate} />
          ) : null}
        </Stack>
        <Divider />
        <Stack gap={4}>
          <Text as="h2" variant="h2">
            Charges
          </Text>
          {/* The payment screen's list, read-only: the same lines there. */}
          <LineItemList
            items={invoice.items.map((item) => ({
              description: item.description,
              qty: item.quantity,
              rate: item.unitPrice,
            }))}
            total={invoice.total}
          />
        </Stack>
        {invoice.invoiceUrl ? (
          <Button
            variant="secondary"
            size="xl"
            pill
            fullWidth
            leadingIcon={<ExternalLink />}
            onClick={() => openExternal(invoice.invoiceUrl!)}
          >
            Open the invoice
          </Button>
        ) : null}
      </Stack>
    </MobileScreen>
  );
}
